import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { EventEmitter, Input, Output} from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FileSetEntryViewModel, DocumentOrganizerType, ColumnModel, InitErrors, StatePropertyValueModel } from '../shared/fs.model';
import { FileSetError, TargetType } from '../services/validation/validator.service';
import { lfFilesetLocalize } from '../shared/utils';
import { LocalizePipe } from '@lf-web-components/lf-common-utils-library';

export interface TableViewModel{
  columns: any[], 
  types: any[],
}

/**
 * Default min-widths for different columns
 */
const COLUMN_MIN_WIDTH = {
  'name': 120, 
  'lastModifiedBy': 120,
  'lastModified': 100,
  'checkedOutBy': 120,
  'createdBy': 120,
  'creationDate': 100, 
  'status': 100,
  'electronicFileLastModified': 100, 
  'entryId': 70,
  'extension': 70,
  'hasTags': 70,
  'indexed': 65,
  'latestVersion': 105,
  'locationDescription': 155,
  'pages': 55,
  'template': 75,
  'totalDocumentSize': 145
};

/**
 * Default max-widths for different columns
 */
 const COLUMN_MAX_WIDTH = {
  'name': 300, 
  'lastModifiedBy': 250,
  'lastModified': 200,
  'checkedOutBy': 250,
  'createdBy': 250,
  'creationDate': 200,
  'status': 200,
  'electronicFileLastModified': 200,
  'entryId': 80,
  'extension': 80,
  'hasTags': 70,
  'indexed': 65,
  'latestVersion': 105,
  'locationDescription': 300,
  'pages': 60,
  'template': 200,
  'totalDocumentSize': 145
};

@Component({
  selector: 'app-organizer-table',
  templateUrl: './organizer-table.component.html',
  styleUrls: ['./organizer-table.component.css', '../global-styles.css']
})
export class OrganizerTableComponent implements OnInit {

  @Input() docColumns: ColumnModel[];
  @Input() docResources: FileSetEntryViewModel[];
  @Input() docStatuses: any[];
  @Input() docTypes: DocumentOrganizerType[];
  @Input() waBaseURL: string;
  @Input() repoName: string;
  @Input() isReadonly: boolean;
  @Input() initError: InitErrors = InitErrors.NONE;
  @Input() errors: FileSetError[];
  @Input() requiredStatus: StatePropertyValueModel;
  @Input() width: number;
  @Input() maxHeight: number;
  @Input() importMode: number;
  @Input() showErrors: boolean;

  @Output() validate = new EventEmitter<any>();
  @Output() onModelChange = new EventEmitter();
  @Output() onImport = new EventEmitter<string>();

  lfFilesetLocalizeHelper = lfFilesetLocalize;
  typeTotalIndexCache: {};
  statusTable: {} = {};
  tTable: {};

  viewModel: TableViewModel = {
    columns: [], 
    types: []
  };

  get initErrorMsg(): string {
    switch(this.initError) {
      case InitErrors.RESOURCE_DELETED:
        return this.localizePipe.transform('lf_fileset_deleted_message', this.lfFilesetLocalizeHelper);
      case InitErrors.RESOURCE_NOT_FOUND:
        return this.localizePipe.transform('lf_fileset_not_found_message', this.lfFilesetLocalizeHelper);
      case InitErrors.NONE_INITIALIZED:
        return this.localizePipe.transform('lf_fileset_no_fileset_message', this.lfFilesetLocalizeHelper);
      case InitErrors.REJECT_PUBLIC_USERS:
        return this.localizePipe.transform('lf_fileset_reject_public_user_message', this.lfFilesetLocalizeHelper);
      default: 
        return "";
    }
  }

  constructor(
    private localizePipe: LocalizePipe
  ) { }

  getColumnStyle(key: string)  {
    if (key in COLUMN_MIN_WIDTH && key in COLUMN_MAX_WIDTH) {
      if (key === 'status') {
        return {
          'min-width': COLUMN_MIN_WIDTH[key] + 'px',
          'max-width': COLUMN_MAX_WIDTH[key] + 'px'
        };
      }
      return {
        'min-width': COLUMN_MIN_WIDTH[key] + 'px',
        'max-width': COLUMN_MAX_WIDTH[key] + 'px',
        'white-space': 'nowrap',
        'overflow': 'hidden',
        'text-overflow': 'ellipsis'
      };
    } else {
      return {
        'min-width': 'auto',
        'max-width': 'auto'
      };
    }
  }

  hasStatusDropdown(isStatusCol): boolean {
    if (!isStatusCol) {
      let statusCol = this.viewModel.columns.find(col => col.key == 'status');

      if (!this.isReadonly && statusCol && this.docStatuses.length > 1) {
        return true;
      }
    }
    return false;
  }

  hasInitError() {
    return this.initError != InitErrors.NONE;
  }

  selectAllDocs(inValue: boolean) {
    this.docResources.forEach(d => d.viewModel.isSelected = inValue)
  }

  ngOnInit(): void {
    this.viewModelPreprocess(this.docResources);
  }

  getStatusDisplay(key: string) {
    var status = this.statusTable[key];
    if (status) {
      return status.display;
    } 
    else 
    {
      return "";
    }
  }

  getColspan(): number {
    return this.viewModel.columns.length + 3;
  }

  modelIsReady(): boolean {
    return this.docTypes.length > 0 &&
    this.docStatuses.length > 0;
  }

  viewModelPreprocess(model: FileSetEntryViewModel[]) {
    
    model.forEach(m => {
        m.viewModel = {
          isSelected: false, 
          typeIndex: 0, 
        }

    });

    this.viewModel = this.processData();

  }

  processData() {

    this.docStatuses.forEach(status =>{
      this.statusTable[status.key] = status;
    });

    var tModel = [];

    this.tTable = {
      '_none_': []
    };

    this.docTypes.forEach(type =>{
      this.tTable[type.key] = [];

      tModel.push({
        name: type.display, 
        id: type.key,
        min: type.min,
        max: type.max,
        files: []
      });
    });

    tModel.push({
      name: this.localizePipe.transform('lf_fileset_none_type', this.lfFilesetLocalizeHelper), 
      files: [], 
      id: "_none_"
    });

    this.docResources.forEach(doc =>{
      if (doc.docType in this.tTable) {
        this.tTable[doc.docType].push(doc);
      }
      else 
      {
        this.tTable['_none_'].push(doc);
      }
    });

    tModel.forEach(m =>{
      m.files = this.tTable[m.id]
    });

    return {
      columns: this.docColumns, 
      types: tModel
    };
  }

  rowDropped(event: CdkDragDrop<string[]>) {

    let fromContainerId = event.previousContainer.data['id'];
    let toContainerId = event.container.data['id'];

    let movedItem = this.tTable[fromContainerId][event.previousIndex];
    
    if (event.container == event.previousContainer) {
      // move to sorted position
      moveItemInArray(event.container.data['files'], event.previousIndex, event.currentIndex);
    } else {
      movedItem.docType = toContainerId;
      transferArrayItem(event.previousContainer.data['files'],
        event.container.data['files'],
        event.previousIndex,
        event.currentIndex);

        this.validate.emit({ data: fromContainerId, type: TargetType.FileType });
        this.validate.emit({ data: toContainerId, type: TargetType.FileType });
        this.onModelChange.emit();
    }
  }

  ngOnChanges() {
    this.refreshTable();
  }

  refreshTable() {
    this.viewModelPreprocess(this.docResources);  
  }

  isTypeRequired(typeKey) {
    let type = this.docTypes.find(x => x.key == typeKey);
    return type === undefined ? false : type.isRequired();
  }

  onOpenDocument(docId){
    let url = `${this.waBaseURL}/DocView.aspx?db=${this.repoName}&docid=${docId}`;
    window.open(url, 
      'popup', 'location=no,toolbar=no,menubar=no,scrollbars=no,status=no,width=1000,height=800');    
  }

  onStatusChange(file: FileSetEntryViewModel) {
    if (this.requiredStatus) {
      const files = this.docResources.filter(d => d.id === file.id);

      if (files.length && files.length > 1) {
        this.validate.emit({ data: files, type: TargetType.File, validateDuplicates: true });
      } else {
        this.validate.emit({ data: file, type: TargetType.File });
      }
      
    }
    this.onModelChange.emit();
  }

  onInlineRemove(dArray, dFile, index) {
    
    dArray.splice(index, 1);

    let docIndex = this.docResources.indexOf(dFile);
    if (docIndex > -1) {
      this.docResources.splice(docIndex, 1);
      
      this.validate.emit({ data: dFile.docType, type: TargetType.FileType });
      this.validate.emit({ data: dFile.id, type: TargetType.File, removeError: true });
      this.onModelChange.emit();
    }
  }

  onTypeImport(typeId) {
    this.onImport.emit(typeId);
  }

  hasTypeError(type) {
    if (this.showErrors) {
      const error = this.errors.find(e => e.target === type.id);
      
      if (error) {
        return true;
      }
    }
    return false;
  }

  hasFileError(file: FileSetEntryViewModel): boolean {
    if (this.showErrors) {
      if (this.requiredStatus) {
        const error = this.errors.find(e => e.target === file.id);
        
        if (error && file.status !== this.requiredStatus?.Key) {
          return true;
        }
        return false;
      }
    }
    return false;
  }

}

